import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    React.useEffect(() => {
        document.title = "Reset Password | Loomara";
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!token) {
            toast("Missing reset token. Use the link from your email.");
            return;
        }
        if (password.length < 6) {
            toast("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            toast("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setDone(true);
            toast("Password updated. You can sign in now.");
            setTimeout(() => navigate("/login", { replace: true }), 2000);
        } catch (err) {
            toast(formatApiError(err.response?.data?.detail) || "Could not reset password");
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <section className="min-h-[80vh] flex items-center justify-center container-boutique py-16" data-testid="reset-password-page">
                <section className="card-soft p-10 w-full max-w-md text-center">
                    <p className="text-sm text-ink-600">This reset link is invalid. Request a new one.</p>
                    <Link to="/forgot-password" className="text-sage-700 underline underline-offset-4 text-sm mt-4 inline-block">
                        Forgot password
                    </Link>
                </section>
            </section>
        );
    }

    return (
        <section className="min-h-[80vh] flex items-center justify-center container-boutique py-16" data-testid="reset-password-page">
            <section className="card-soft p-10 w-full max-w-md">
                <p className="eyebrow text-center">Account</p>
                <h1 className="font-serif text-4xl text-ink-900 text-center mt-2 mb-8">New password</h1>
                {done ? (
                    <p className="text-sm text-ink-600 text-center">Redirecting to sign in…</p>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <input
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            type="password"
                            required
                            minLength={6}
                            placeholder="New password"
                            className="w-full bg-ivory-100 rounded-xl px-4 py-3 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300"
                            data-testid="reset-password"
                        />
                        <input
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            type="password"
                            required
                            minLength={6}
                            placeholder="Confirm password"
                            className="w-full bg-ivory-100 rounded-xl px-4 py-3 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300"
                            data-testid="reset-password-confirm"
                        />
                        <button disabled={loading} className="btn-primary w-full" data-testid="reset-submit">
                            {loading ? "Saving…" : "Update password"}
                        </button>
                    </form>
                )}
                <p className="text-center text-sm text-ink-500 mt-6">
                    <Link to="/login" className="text-sage-700 underline underline-offset-4">Back to sign in</Link>
                </p>
            </section>
        </section>
    );
}
